import { useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Col, Modal, Row, Tooltip } from "antd";
import csvImport from "../../assets/svg/csv-import.svg";
import gmail from "../../assets/images/auth/gmail.svg";
import microsoft from "../../assets/images/auth/microsoft.png";
import beyonData from "../../assets/svg/logos/logo-small.svg";
import linkedin from "../../assets/images/integration/Linkedin.svg";
import CSVImport from "./CSVImport";
import { useImport } from "./hooks/useImport";
import paperShredder from "../../assets/images/webToLead/paper-shredder.svg";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Loader from "../loader";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import upload from "../../assets/svg/icons/upload.svg";

const ImportLead = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCSVImport, handleConnect, handleDisconnect, handleLatestSync } =
    useImport();
  const [isCsvImport, setCSVImport] = useState(false);
  const [isDisconnect, setDisconnect] = useState(false);
  const [type, setType] = useState();
  const summary = useSelector((state) => state.importReducer.summary);
  const loading = useSelector((state) => state.importReducer.loading);
  const isCSVConnected =
    summary?.sources?.find((d) => d?.name === "CSV/XLSX Import")?.status ===
    "Connected";
  const isGmailConnected =
    summary?.sources?.find((d) => d?.name === "Gmail Import")?.status ===
    "Connected";
  const isBeyonConnected =
    summary?.sources?.find((d) => d?.name === "BeyondLeadz Import")?.status ===
    "Connected";
  const isOutlookConnected =
    summary?.sources?.find((d) => d?.name === "Outlook Import")?.status ===
    "Connected";
  const connectedSourcesCount = summary?.sources?.filter(
    (d) => d?.status === "Connected",
  )?.length;

  const importMethods = [
    {
      title: "CSV Import",
      description: (
        <>
          Upload your own CSV file with lead data
          <div className="tw-pt-1">
            Sample file:{" "}
            <Link
              to="/client-leads.xlsx"
              target="_blank"
              download
              className="text-primary text-decoration-underline"
            >
              .xlsx
            </Link>{" "}
            <Link
              to="/client-leads.csv"
              target="_blank"
              download
              className="text-primary text-decoration-underline"
            >
              .csx
            </Link>
            <span className="tw-text-xs tw-pl-2">(Max. 10 MB)</span>
          </div>
        </>
      ),
      icon: csvImport,
      iconBg: "E5E6FF",
      status: isCSVConnected ? "Connected" : "Not Connected",
      maxSize: "10 MB",
      action: isCSVConnected ? "Disconnect" : "Upload CSV",
      isSyncLatest: false,
      onClick: () => {
        if (isCSVConnected) {
          setDisconnect(true);
          setType("CSV");
        } else {
          setCSVImport(true);
        }
      },
    },
    {
      title: "Gmail",
      description: "Import contacts from Gmail",
      icon: gmail,
      iconBg: "E2FFF8",
      status: isGmailConnected ? "Connected" : "Not Connected",
      action: isGmailConnected ? "Disconnect" : "Import Gmail Contacts",
      isSyncLatest: !!isGmailConnected,
      onClick: () => {
        if (isGmailConnected) {
          setDisconnect(true);
          setType("Gmail");
        } else {
          handleConnect("Gmail");
        }
      },
      onSync: () => {
        handleLatestSync("Gmail");
      },
    },
    {
      title: "Outlook",
      description: "Import contacts from Microsoft Outlook",
      icon: microsoft,
      iconBg: "FFE3D9",
      status: isOutlookConnected ? "Connected" : "Not Connected",
      action: isOutlookConnected ? "Disconnect" : "Import Outlook Contacts",
      isSyncLatest: !!isOutlookConnected,
      onClick: () => {
        if (isOutlookConnected) {
          setDisconnect(true);
          setType("Outlook");
        } else {
          handleConnect("Outlook");
        }
      },
      onSync: () => {
        handleLatestSync("Outlook");
      },
    },
    {
      title: "BeyondLeadz Data",
      description: "Access our proprietary lead database",
      icon: beyonData,
      iconBg: "D1F6FF",
      isSyncLatest: false,
      status: isBeyonConnected ? "Connected" : "Not Connected",
      action: isBeyonConnected ? "Disconnect" : "Explore Database",
      onClick: () => {
        if (isBeyonConnected) {
          setDisconnect(true);
          setType("Beyondleadz data");
        } else {
          searchParams.set("status", "beyondleadzData");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
      },
    },
    {
      title: "LinkedIn",
      description: "Import connections and leads from LinkedIn Sales Navigator",
      icon: linkedin,
      iconBg: "fff",
      status: "Not Connected",
      action: "Import from linkedin",
    },
  ];

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <CSVImport
        open={isCsvImport}
        handleCSVImport={handleCSVImport}
        handleCancel={() => setCSVImport(false)}
      />

      <Row gutter={[16, 16]} className="mt50">
        {importMethods?.slice(0, 3)?.map((method, index) => {
          return (
            <Col xs={24} md={12} lg={8} key={index}>
              <div className="review-card card position-relative h-100 d-flex flex-column justify-content-between gap-20 p-20">
                <div className="d-flex align-items-start justify-content-between gap-10">
                  <div className="d-flex gap-10">
                    <div>
                      <Badge
                        dot
                        color={method.status === "Connected" ? "green" : !method.onClick ? "orange" : "red"}
                        offset={[-5, 5]}
                        className="big-dot"
                      >
                        <div
                          className={`d-flex align-items-center tw-bg-secondary-color tw-rounded-full justify-content-center h-42 w-42`}
                        >
                          <img
                            src={method.icon}
                            alt={method.title}
                            className="tw-w-[20px]"
                          />
                        </div>
                      </Badge>
                    </div>
                    <div>
                      <div className="tw-font-semibold tw-text-sm tw-text-black tw-flex tw-items-center tw-gap-2">
                        <span>{method.title}</span>
                      </div>
                      <div className="tw-text-xs tw-text-sub-text tw-pt-1">
                        {method.description}
                      </div>
                    </div>
                  </div>
                  {!method.onClick && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-[11px] tw-font-bold tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-solid tw-border-amber-200/80 tw-shadow-2xs tw-whitespace-nowrap">
                      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-500 tw-animate-pulse" />
                      Coming Soon
                    </span>
                  )}
                </div>

                <div className="response tw-mt-5 d-flex align-items-center justify-content-between tw-gap-3">
                  {method.isSyncLatest && (
                    <Button
                      className="light-btn tw-w-full"
                      onClick={method.onSync}
                    >
                      Sync Latest Contacts
                    </Button>
                  )}

                  {method.onClick ? (
                    <Button
                      className="tw-shadow-none !tw-bg-secondary-color tw-font-semibold tw-rounded-[10px] !tw-text-black !tw-text-xs tw-w-full"
                      type="primary"
                      block
                      onClick={method.onClick}
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        {method?.title !== "BeyondLeadz Data" &&
                          method?.action !== "Disconnect" && (
                            <img src={upload} alt="" />
                          )}
                        {method.action}
                      </div>
                    </Button>
                  ) : (
                    <FeatureComingSoon className="tw-w-full">
                      <Button
                        className="tw-shadow-none !tw-bg-secondary-color tw-font-semibold tw-rounded-[10px] !tw-text-black !tw-text-xs tw-w-full"
                        type="primary"
                        block
                      >
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                          <img src={upload} alt="" />
                          {method.action}
                        </div>
                      </Button>
                    </FeatureComingSoon>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      <div className="tw-text-lg tw-font-semibold tw-mt-10">
        Advanced & Enrichment
      </div>
      <div className="tw-text-sm tw-text-sub-text">
        Access premium data sources and integrations
      </div>
      <Row gutter={[16, 16]} className="tw-mt-6">
        {importMethods?.slice(3, 5)?.map((method, index) => {
          return (
            <Col xs={24} md={12} lg={8} key={index}>
              <div className="review-card card position-relative h-100 d-flex flex-column justify-content-between gap-20 p-20">
                <div className="d-flex align-items-start justify-content-between gap-10">
                  <div className="d-flex gap-10">
                    <div>
                      <Badge
                        dot
                        color={method.status === "Connected" ? "green" : !method.onClick ? "orange" : "red"}
                        offset={[-5, 5]}
                        className="big-dot"
                      >
                        <div
                          className={`d-flex align-items-center tw-bg-secondary-color tw-rounded-full justify-content-center h-42 w-42`}
                        >
                          <img src={method.icon} alt={method.title} />
                        </div>
                      </Badge>
                    </div>
                    <div>
                      <div className="tw-font-semibold tw-text-sm tw-text-black tw-flex tw-items-center tw-gap-2">
                        <span>{method.title}</span>
                      </div>
                      <div className="tw-text-xs tw-text-sub-text tw-pt-1">
                        {method.description}
                      </div>
                    </div>
                  </div>
                  {!method.onClick && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-[11px] tw-font-bold tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-solid tw-border-amber-200/80 tw-shadow-2xs tw-whitespace-nowrap">
                      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-500 tw-animate-pulse" />
                      Coming Soon
                    </span>
                  )}
                </div>

                <div className="response tw-mt-5 d-flex align-items-center justify-content-between tw-gap-3">
                  {method.isSyncLatest && (
                    <Button
                      className="light-btn tw-w-full"
                      onClick={method.onSync}
                    >
                      Sync Latest Contacts
                    </Button>
                  )}

                  {method.onClick ? (
                    <Button
                      className="tw-shadow-none !tw-bg-secondary-color tw-rounded-[10px] !tw-text-black !tw-text-xs tw-w-full"
                      type="primary"
                      block
                      onClick={method.onClick}
                    >
                      <div className="tw-flex tw-items-center tw-font-semibold tw-justify-center tw-gap-2">
                        {method?.title !== "BeyondLeadz Data" &&
                          method?.action !== "Disconnect" && (
                            <img src={upload} alt="" />
                          )}
                        {method.action}
                      </div>
                    </Button>
                  ) : (
                    <FeatureComingSoon className="tw-w-full">
                      <Button
                        className="tw-shadow-none !tw-bg-secondary-color tw-font-semibold tw-rounded-[10px] !tw-text-black !tw-text-xs tw-w-full"
                        type="primary"
                        block
                      >
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                          <img src={upload} alt="" />
                          {method.action}
                        </div>
                      </Button>
                    </FeatureComingSoon>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      <div className="d-flex justify-content-end align-items-end mt30">
        <Tooltip title={connectedSourcesCount === 0 && "No sources connected"}>
          <Button
            type="primary"
            className="theme-btn"
            size="large"
            disabled={connectedSourcesCount === 0}
            onClick={() => {
              searchParams.set("status", "preview");
              navigate(`${location.pathname}?${searchParams.toString()}`);
            }}
          >
            Continue to Data Selection →
          </Button>
        </Tooltip>
      </div>

      <Modal
        title={null}
        className="delete-popup"
        open={isDisconnect}
        centered
        footer={null}
        onCancel={() => {
          setDisconnect(false);
          setType();
        }}
      >
        <div className="tw-bg-secondary-color tw-text-text-primary tw-text-lg tw-font-semibold tw-py-4 tw-px-6 tw-rounded-t-2xl">
          Disconnect Import
        </div>
        <div style={{ padding: "20px 24px" }} className="web-to-lead mt10">
          <div className="d-flex align-items-center" style={{ gap: 50 }}>
            <img src={paperShredder} alt="" />
            <div className="text-color-434040 heading16px mt10">
              <div>Are you sure you want to disconnect?</div>
              <div className="mt10">
                {type === "CSV"
                  ? "Imported leads will remain, Your data won’t be imported."
                  : type === "Beyondleadz data"
                    ? "Imported leads will remain, Your data won’t be imported."
                    : "Removing your email account will halt all future contact tracking and synchronization."}
              </div>
            </div>
          </div>
        </div>
        <div className="tw-flex tw-bg-secondary-color tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-4">
          <Button
            className="tw-border-sub-text hover:!tw-border-sub-text tw-text-sm tw-text-sub-text hover:!tw-text-sub-text"
            onClick={() => {
              setDisconnect(false);
              setType();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
            onClick={() => {
              handleDisconnect(type);
              setDisconnect(false);
              setType();
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ImportLead;
